import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { publicApi, formatAuthError } from "../lib/api";
import { Logo } from "../components/Logo";
import { PublicFooter } from "../components/PublicFooter";

/**
 * Forgot password — single-screen email capture.
 *
 * The backend never reveals whether an email is registered, so this page
 * always shows the same success state regardless. That prevents enumeration.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await publicApi.post("/auth/password-reset/request", { email: email.trim().toLowerCase() });
            setSubmitted(true);
        } catch (err) {
            setError(formatAuthError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            className="relative min-h-screen w-full bg-[#0a0a0a] flex items-center justify-center px-6 py-28"
            data-testid="forgot-password-page"
        >
            <div className="absolute top-8 left-8 z-10">
                <Logo />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="relative z-10 w-full max-w-[400px]"
            >
                <Link
                    to="/login"
                    data-testid="forgot-back-to-login"
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500 hover:text-zinc-200 transition-colors mb-8"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to sign in
                </Link>
                <p className="text-[11.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
                    Account recovery
                </p>
                <h1 className="text-3xl font-semibold tracking-tight text-zinc-50">
                    Reset your password
                </h1>
                <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed">
                    Enter the email you use to sign in. We'll send a secure link to set a new password.
                </p>

                {!submitted ? (
                    <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="forgot-form">
                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-[12px] text-zinc-400 font-medium">
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="forgot-email-input"
                                placeholder="you@business.com"
                                className="w-full h-11 rounded-md bg-zinc-950/60 border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-all focus:border-zinc-500 focus:bg-zinc-900/60"
                            />
                        </div>

                        {error && (
                            <div
                                className="text-[13px] text-red-400/90 border border-red-900/40 bg-red-950/30 rounded-md px-3 py-2"
                                data-testid="forgot-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="forgot-submit-button"
                            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-md bg-zinc-50 text-zinc-950 text-[14px] font-medium hover:bg-white disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Sending
                                </>
                            ) : (
                                <>
                                    Send reset link
                                    <ArrowRight className="h-4 w-4" />
                                </>
                            )}
                        </button>
                    </form>
                ) : (
                    <div
                        className="mt-8 rounded-lg border border-zinc-800 bg-zinc-950/60 p-5"
                        data-testid="forgot-success"
                    >
                        <p className="text-[13.5px] text-zinc-200 font-medium">
                            Check your inbox.
                        </p>
                        <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">
                            If <span className="text-zinc-200">{email}</span> belongs to a WebJesters
                            account, we just sent a secure reset link. It expires in 30 minutes.
                        </p>
                        <p className="mt-3 text-[12px] text-zinc-500">
                            Didn't get it? Check spam, or wait a minute and try again.
                        </p>
                    </div>
                )}
            </motion.div>
            <PublicFooter prefix="forgot-password" />
        </div>
    );
}
